import { useEffect, useState } from 'react'

import { Box, Button, RadioButton, SelectOption, Text } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { ImportResultModalDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { COMPENSATIONS_IMPORT_THRESHOLD_MAPPING } from 'modules/compensations/lib/constants'
import NuvoImporter from 'modules/universal_importer/components/NuvoImporter'

interface ImportResultModalProps {
  onClose?: () => void
  payrollRunId?: number
  source?: string
}

export const ImportResultModal = ({
  payrollRunId,
  onClose,
  source = 'compensations',
}: ImportResultModalProps) => {
  const {
    company,
    access: { locale },
  } = useCurrentSession()

  const [fetchPayrollRuns, { data }] = useLazyQuery(ImportResultModalDocument)
  const [payrollRunIdSelected, setPayrollRunIdSelected] = useState<number | undefined>(payrollRunId)
  const [radioButtonType, setRadioButtonType] = useLocalStorage<'rows' | 'columns'>(
    LOCAL_STORAGE_KEYS.compensationsImportResultModalProcessBy,
    'rows'
  )
  const payrollRuns = data?.compensations?.payrollRunsConnection?.nodes ?? []

  useEffect(() => {
    if (payrollRunId === undefined) fetchPayrollRuns()
  }, [])

  useEffect(() => {
    const noPayrollRunProvided = payrollRunId === undefined
    const noPayrollRunSelected = !payrollRunIdSelected

    if (noPayrollRunProvided && noPayrollRunSelected) {
      const firstPayrollRun = data?.compensations?.payrollRunsConnection?.nodes?.[0]

      if (firstPayrollRun) {
        setPayrollRunIdSelected(firstPayrollRun.id)
      }
    }
  }, [data, payrollRunId, payrollRunIdSelected])

  const periodOptions: SelectOption[] = payrollRuns.map((period) => ({
    label: period.label || period.id.toString(),
    value: period.id.toString(),
  }))

  const handlePeriodSelectChange = (value: string) => {
    setPayrollRunIdSelected(parseInt(value))
  }

  const handleRadioButtonChange = (value: 'rows' | 'columns') => {
    setRadioButtonType(value)
  }

  const handleFileUpload = (_file: File) => {
    track(trackingEvents.payroll.selectResultFile, {
      source,
    })
  }

  const handleDownloadTemplate = () => {
    track(trackingEvents.payroll.downloadTemplate, {
      source,
      company_id: company.id,
    })
  }

  const handleCompleted = () => {
    track(trackingEvents.compensations.completeImportCompensations, {
      import_type: source,
      payroll_run_id: payrollRunIdSelected,
    })
  }

  const handleError = (error_message?: string) => {
    track(trackingEvents.compensations.errorImportCompensations, {
      import_type: source,
      payroll_run_id: payrollRunIdSelected,
      error_message,
    })
  }

  return (
    <Modal
      illustration="table-file"
      title={i18n.t('compensations.PayrollRunCompensationsList.import_result.modal.title')}
      onClose={onClose}
      disableFocusTrap
      footer={
        <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
        </Box>
      }
    >
      <Box gap="s24">
        <Box gap="s12">
          <Text.Normal weight="bold">
            {i18n.t(
              'compensations.PayrollRunCompensationsList.import_result.modal.process_by.title'
            )}
          </Text.Normal>
          <RadioButton
            value="rows"
            label={i18n.t(
              'compensations.PayrollRunCompensationsList.import_result.modal.process_by.rows'
            )}
            checked={(radioButtonType || 'rows') === 'rows'}
            onChange={() => handleRadioButtonChange('rows')}
          />
          <RadioButton
            value="columns"
            label={i18n.t(
              'compensations.PayrollRunCompensationsList.import_result.modal.process_by.columns'
            )}
            checked={(radioButtonType || 'rows') === 'columns'}
            onChange={() => handleRadioButtonChange('columns')}
          />
        </Box>
        {periodOptions.length > 0 && (
          <Select
            label={i18n.t('compensations.PayrollRunCompensationsList.import_result.modal.period')}
            options={periodOptions}
            onChange={handlePeriodSelectChange}
            value={payrollRunIdSelected?.toString()}
          />
        )}
        {payrollRunIdSelected && (
          <NuvoImporter
            importType="payroll_summary_importer"
            locale={locale}
            companyId={company.id}
            headerSelection
            metadata={{ payroll_run_id: [payrollRunIdSelected] }}
            processBy={radioButtonType || 'rows'}
            onUploadedFile={handleFileUpload}
            onDownloadTemplate={handleDownloadTemplate}
            onCompleted={() => {
              handleCompleted()
            }}
            onError={handleError}
            thresholdMapping={COMPENSATIONS_IMPORT_THRESHOLD_MAPPING}
          />
        )}
      </Box>
    </Modal>
  )
}
